/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { PaymentGatewayState, PaymentGateway } from '../../interface/payment-gateway.interface';
import paymentGatewayApi from '../../utils/api/payment-gateway';

const initialState: PaymentGatewayState = {
  gateways: [],
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchGateways = createAsyncThunk(
  'paymentGateway/fetchGateways',
  async (_, { rejectWithValue }) => {
    try {
      const response = await paymentGatewayApi.getGateways();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch gateways');
    }
  },
);

export const toggleGateway = createAsyncThunk(
  'paymentGateway/toggleGateway',
  async ({ id, isActive }: { id: string; isActive: boolean }, { rejectWithValue }) => {
    try {
      await paymentGatewayApi.toggleGatewayStatus(id, isActive);
      return { id, isActive };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to toggle gateway');
    }
  },
);

const paymentGatewaySlice = createSlice({
  name: 'paymentGateway',
  initialState,
  reducers: {
    // Local reducers if needed
  },
  /**
   * extraReducers
   * @param {any} builder - The builder
   */
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchGateways.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGateways.fulfilled, (state, action: PayloadAction<PaymentGateway[]>) => {
        state.isLoading = false;
        state.gateways = action.payload;
      })
      .addCase(fetchGateways.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Toggle
      .addCase(toggleGateway.fulfilled, (state, action) => {
        const { id, isActive } = action.payload;
        if (isActive) {
          // If turning one ON, turn others OFF
          state.gateways = state.gateways.map((g) => ({
            ...g,
            isActive: g.id === id,
            status: g.id === id ? 'Active' : 'Inactive',
          }));
        } else {
          // Just turn this one OFF
          const index = state.gateways.findIndex((g) => g.id === id);
          if (index !== -1) {
            state.gateways[index].isActive = false;
            state.gateways[index].status = 'Inactive';
          }
        }
      });
  },
});

export default paymentGatewaySlice.reducer;
